import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, updateDoc, doc, deleteDoc, setDoc, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import menuOptions from '../config/menuOptions.json';

const defaultMenus = menuOptions;

function GuestList() {
  const [guests, setGuests] = useState([]);
  const [editingClan, setEditingClan] = useState(null);
  const [originalEditingClan, setOriginalEditingClan] = useState(null);
  const [editedGroups, setEditedGroups] = useState({});
  const [failedLogins, setFailedLogins] = useState([]);

  const fetchGuests = async () => {
    const guestCollection = collection(db, 'invites');
    const guestSnapshot = await getDocs(guestCollection);
    const guestList = guestSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setGuests(guestList);
  };

  const fetchFailedLogins = async () => {
    // Fetch users not on guest list who tried to log in
    const failedLoginsCollection = collection(db, 'loginFails');
    const failedLoginsSnapshot = await getDocs(failedLoginsCollection);
    const failedLoginsList = failedLoginsSnapshot.docs.map(doc => ({
      id: doc.id,
      type: 'not_on_list',
      ...doc.data()
    }));

    // Fetch users who requested a code but never logged in (expired codes)
    const codeRequestsCollection = collection(db, 'loginCodeRequests');
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    const expiredCodeRequestsQuery = query(
      codeRequestsCollection,
      where('completed', '==', false)
    );
    const expiredCodeRequestsSnapshot = await getDocs(expiredCodeRequestsQuery);
    
    const expiredCodeRequestsList = expiredCodeRequestsSnapshot.docs
      .map(doc => {
        const data = doc.data();
        const codeSentAt = data.codeSentAt?.toDate ? data.codeSentAt.toDate() : new Date(data.codeSentAt || 0);
        // Only include if code was sent more than 10 minutes ago
        if (codeSentAt < tenMinutesAgo) {
          return {
            id: doc.id,
            type: 'code_not_used',
            phoneNumber: data.phoneNumber,
            name: data.name,
            attemptedAt: data.codeSentAt || codeSentAt
          };
        }
        return null;
      })
      .filter(item => item !== null);

    // Combine both lists
    const allFailedLogins = [...failedLoginsList, ...expiredCodeRequestsList];
    
    // Sort by attemptedAt/codeSentAt, most recent first
    allFailedLogins.sort((a, b) => {
      const aTime = a.attemptedAt?.toDate ? a.attemptedAt.toDate() : new Date(a.attemptedAt || 0);
      const bTime = b.attemptedAt?.toDate ? b.attemptedAt.toDate() : new Date(b.attemptedAt || 0);
      return bTime - aTime;
    });
    
    setFailedLogins(allFailedLogins);
  };

  useEffect(() => {
    fetchGuests();
    fetchFailedLogins();
  }, []);

  // Group guests by familyClan
  const groupedGuests = guests.reduce((acc, guest) => {
    const clan = guest.familyClan || 'Unknown';
    if (!acc[clan]) acc[clan] = [];
    acc[clan].push(guest);
    return acc;
  }, {});

  // Handle input changes for edited guests
  const handleGuestChange = (clan, guestId, field, value) => {
    setEditedGroups(prev => {
      const updatedGroup = prev[clan].map(g => {
        if (g.id === guestId) {
          if (field === 'photo') {
            return { ...g, photo: value, photoUrl: value ? URL.createObjectURL(value) : '' };
          }
          return { ...g, [field]: value };
        }
        return g;
      });
      return { ...prev, [clan]: updatedGroup };
    });
  };

  // Handle menu choice changes
  const handleMenuChoiceChange = (clan, guestId, course, value) => {
    setEditedGroups(prev => {
      const updatedGroup = prev[clan].map(g => {
        if (g.id === guestId) {
          return {
            ...g,
            menuChoices: {
              ...g.menuChoices,
              [course]: value
            }
          };
        }
        return g;
      });
      return { ...prev, [clan]: updatedGroup };
    });
  };

  // Handle dietary requirements changes (local state only, not saved yet)
  const handleDietaryRequirementsInputChange = (clan, guestId, hasRequirements, requirements) => {
    setEditedGroups(prev => {
      const updatedGroup = prev[clan].map(g => {
        if (g.id === guestId) {
          return {
            ...g,
            _dietaryRequirementsInput: {
              hasRequirements,
              requirements: requirements || ''
            }
          };
        }
        return g;
      });
      return { ...prev, [clan]: updatedGroup };
    });
  };

  // Save dietary requirements for a guest
  const handleDietaryRequirementsSubmit = async (clan, guestId) => {
    const guest = editedGroups[clan]?.find(g => g.id === guestId);
    if (!guest) {
      console.error('Guest not found:', guestId);
      return;
    }

    const input = guest._dietaryRequirementsInput;
    // If no input state, use current guest values
    const hasRequirements = input?.hasRequirements ?? guest.hasDietaryRequirements ?? false;
    const requirements = (input?.requirements ?? guest.dietaryRequirements ?? '').trim();

    console.log('Saving dietary requirements:', { guestId, hasRequirements, requirements, input, guest });

    try {
      const guestRef = doc(db, 'invites', guestId);
      const updateData = {
        hasDietaryRequirements: hasRequirements,
        dietaryRequirements: hasRequirements ? requirements : ''
      };
      console.log('Update data:', updateData);
      await updateDoc(guestRef, updateData);
      console.log('Successfully saved dietary requirements');

      // Update local state and remove input state
      setEditedGroups(prev => {
        const updatedGroup = prev[clan].map(g => {
          if (g.id === guestId) {
            const updated = { ...g };
            updated.hasDietaryRequirements = hasRequirements;
            updated.dietaryRequirements = hasRequirements ? requirements : '';
            delete updated._dietaryRequirementsInput;
            return updated;
          }
          return g;
        });
        return { ...prev, [clan]: updatedGroup };
      });

      // Refresh guests to get updated data
      await fetchGuests();
      alert('Dietary requirements saved successfully!');
    } catch (error) {
      console.error('Error saving dietary requirements:', error);
      alert('Failed to save dietary requirements. Please try again.');
    }
  };

  // Handle deleting a guest from edited group: move to deletedInvites, then remove from editedGroups and delete from invites
  const handleDeleteGuest = async (clan, guestId) => {
    const guestToDelete = editedGroups[clan]?.find(g => g.id === guestId);
    if (!guestToDelete) return;

    const deletedRef = doc(db, 'deletedInvites', guestId);
    await setDoc(deletedRef, guestToDelete);
    const originalRef = doc(db, 'invites', guestId);
    await deleteDoc(originalRef);

    setEditedGroups(prev => {
      const updatedGroup = prev[clan].filter(g => g.id !== guestId);
      return { ...prev, [clan]: updatedGroup };
    });
    
    await fetchGuests();
  };

  // Handle deleting an entire family group
  const handleDeleteFamilyGroup = async (clan) => {
    if (!window.confirm(`Are you sure you want to delete the entire "${clan}" family group? This will delete all ${groupedGuests[clan]?.length || 0} guests in this group.`)) {
      return;
    }

    const group = groupedGuests[clan] || [];
    
    try {
      // Move all guests to deletedInvites and delete from invites
      for (const guest of group) {
        const deletedRef = doc(db, 'deletedInvites', guest.id);
        await setDoc(deletedRef, guest);
        const originalRef = doc(db, 'invites', guest.id);
        await deleteDoc(originalRef);
      }
      
      await fetchGuests();
      alert(`Successfully deleted the "${clan}" family group.`);
    } catch (error) {
      console.error('Error deleting family group:', error);
      alert('Failed to delete family group. Please try again.');
    }
  };

  // Helper function to check if age category is adult
  const isAdult = (category) => category === 'adult';

  // Handle adding a new member to an existing family group
  const handleAddNewMember = async (clan) => {
    const group = editedGroups[clan] || [];
    if (group.length === 0) {
      alert('Cannot add member: group data not found.');
      return;
    }

    // Get familyId from existing group members (they should all have the same familyId)
    const familyId = group[0]?.familyId || uuidv4();

    // Create a new guest object
    const newGuest = {
      id: `temp-${Date.now()}`, // Temporary ID for local state
      name: '',
      phoneNumber: '',
      ageCategory: 'adult',
      role: 'guest',
      tableNumber: '',
      familyClan: clan,
      familyId: familyId,
      photoUrl: '',
      comments: '',
      photo: null, // Store file object temporarily
      eveningGuest: false,
      _isNew: true // Flag to indicate this is a new guest
    };

    // Add to edited groups
    setEditedGroups(prev => ({
      ...prev,
      [clan]: [...(prev[clan] || []), newGuest]
    }));
  };

  // Handle saving a new member that was added to the group
  const handleSaveNewMember = async (clan, tempGuestId) => {
    const group = editedGroups[clan] || [];
    const newGuest = group.find(g => g.id === tempGuestId && g._isNew);
    
    if (!newGuest) {
      alert('New guest not found.');
      return;
    }

    // Validate required fields
    if (!newGuest.name || !newGuest.ageCategory) {
      alert('Please fill in at least the name and age category for the new member.');
      return;
    }

    try {
      const storage = getStorage();
      const guestId = uuidv4();
      let photoUrl = '';

      // Upload photo if provided
      if (newGuest.photo) {
        const photoRef = ref(storage, `guest_photos/${guestId}`);
        await uploadBytes(photoRef, newGuest.photo);
        photoUrl = await getDownloadURL(photoRef);
      }

      // Get all adults in the group (including existing ones) to set editableBy
      // Use guestId if available, otherwise use id (Firestore document ID)
      const allAdults = group
        .filter(g => !g._isNew && isAdult(g.ageCategory))
        .map(g => g.guestId || g.id)
        .filter(Boolean);
      
      // If the new guest is an adult, add their ID to editableBy for minors
      if (isAdult(newGuest.ageCategory)) {
        allAdults.push(guestId);
      }

      // Get familyId from existing group members (they should all have the same familyId)
      const familyId = group.find(g => !g._isNew && g.familyId)?.familyId || uuidv4();

      // Prepare the new guest document
      const guestData = {
        guestId: guestId,
        name: newGuest.name,
        phoneNumber: newGuest.phoneNumber || '',
        ageCategory: newGuest.ageCategory,
        role: newGuest.role || 'guest',
        tableNumber: newGuest.tableNumber || '',
        familyClan: clan,
        familyId: familyId,
        photoUrl: photoUrl,
        comments: newGuest.comments || '',
        eveningGuest: newGuest.eveningGuest === true,
        editableBy: isAdult(newGuest.ageCategory) ? [] : allAdults
      };

      // Add to Firestore
      await addDoc(collection(db, 'invites'), guestData);

      // Update existing minors' editableBy to include the new adult if applicable
      if (isAdult(newGuest.ageCategory)) {
        const existingMinors = group.filter(g => !g._isNew && !isAdult(g.ageCategory) && g.id && !g.id.startsWith('temp-'));
        for (const minor of existingMinors) {
          const minorRef = doc(db, 'invites', minor.id);
          const currentEditableBy = minor.editableBy || [];
          if (!currentEditableBy.includes(guestId)) {
            await updateDoc(minorRef, {
              editableBy: [...currentEditableBy, guestId]
            });
          }
        }
      }

      // Remove the new guest from edited groups and refresh
      setEditedGroups(prev => ({
        ...prev,
        [clan]: (prev[clan] || []).filter(g => g.id !== tempGuestId)
      }));

      // Refresh guests to get the new member
      await fetchGuests();
      alert('New member added successfully!');
    } catch (error) {
      console.error('Error adding new member:', error);
      alert('Failed to add new member. Please try again.');
    }
  };

  // Handle removing a new member that hasn't been saved yet
  const handleRemoveNewMember = (clan, tempGuestId) => {
    setEditedGroups(prev => ({
      ...prev,
      [clan]: (prev[clan] || []).filter(g => g.id !== tempGuestId)
    }));
  };

  // Handle clan name change
  const handleClanNameChange = (oldClan, newClan) => {
    setEditedGroups(prev => {
      if (newClan === oldClan) return prev;
      const updatedGroups = { ...prev };
      updatedGroups[newClan] = updatedGroups[oldClan];
      delete updatedGroups[oldClan];
      return updatedGroups;
    });
    // Also update editingClan to newClan so editing continues correctly
    setEditingClan(newClan);
  };

  // Get menu options based on age category
  const getMenuOptions = (course, ageCategory) => {
    const age = (ageCategory || '').toLowerCase();
    const isChild = age === 'child';
    const isTeen = age === 'teen';
    const isAdult = age === 'adult';
    
    if (isChild) {
      // Children can select from both child and adult options
      const childOptions = defaultMenus['Child'][course] || [];
      const adultOptions = defaultMenus['Adult'][course] || [];
      return [...childOptions, ...adultOptions];
    } else if (isTeen || isAdult) {
      // Teens and adults see only adult menu options
      return defaultMenus['Adult'][course] || [];
    }
    return [];
  };

  // Calculate age summary
  const ageSummary = guests.reduce((acc, guest) => {
    const category = guest.ageCategory || 'unknown';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  // Day vs evening guest counts
  const totalDayGuests = guests.filter(g => !g.eveningGuest).length;
  const totalEveningGuests = guests.filter(g => g.eveningGuest).length;

  // Total RSVP'd yes
  const totalRsvpYes = guests.filter(g => g.rsvp === 'yes').length;

  // Total people who have completed all menu choices (starter, main, dessert) — day guests only
  const totalMenuCompleted = guests.filter(g => {
    if (g.eveningGuest) return false;
    const mc = g.menuChoices || {};
    return mc.starter && mc.main && mc.dessert;
  }).length;

  return (
    <div className="page-container">
      <h2 className="page-title">Guest List</h2>
      <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginBottom: '8px' }}>
        <Link to="/user-logs" style={{ color: 'var(--link-color)' }}>View user activity logs →</Link>
      </p>
      <div className="page-subtitle" style={{ marginBottom: '20px' }}>
        <strong style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)', fontWeight: '600' }}>Totals:</strong>
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginTop: '8px' }}>
          Day guests: {totalDayGuests} | Evening guests: {totalEveningGuests}
        </p>
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Adults: {ageSummary.adult || 0} | Teens: {ageSummary.teen || 0} | Children: {ageSummary.child || 0} | Infants: {ageSummary.infant || 0}
        </p>
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginTop: '4px' }}>
          RSVP'd Yes: {totalRsvpYes} | Menu choices completed: {totalMenuCompleted}
        </p>
      </div>
      {Object.entries(groupedGuests)
        .sort(([clanA], [clanB]) => clanA.localeCompare(clanB))
        .map(([clan, group]) => {
        const ageRank = { adult: 4, teen: 3, child: 2, infant: 1 };

        if (originalEditingClan === clan) {
          const editedGroup = editedGroups[editingClan] || [];

          // Sort edited group
          editedGroup.sort((a, b) => (ageRank[b.ageCategory] || 0) - (ageRank[a.ageCategory] || 0));

          return (
            <div key={clan} className="card" style={{ marginBottom: '40px', overflowX: 'auto' }}>
              <input
                type="text"
                value={editingClan}
                onChange={e => handleClanNameChange(editingClan, e.target.value)}
                className="input-field"
                style={{ 
                  fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', 
                  fontWeight: 'bold', 
                  marginBottom: '10px',
                  fontFamily: 'var(--font-heading)',
                  textAlign: 'center'
                }}
              />
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}></th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Name</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Phone Number</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Role</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Table Number</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Age Category</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Evening</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Last Login</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {editedGroup.map(guest => {
                    const isNewMember = guest._isNew;
                    const isEveningGuest = guest.eveningGuest === true;
                    return (
                    <React.Fragment key={guest.id}>
                      <tr style={{ verticalAlign: 'middle', backgroundColor: isNewMember ? 'rgba(255, 255, 0, 0.1)' : 'transparent' }}>
                        <td style={{ display: 'flex', alignItems: 'center' }}>
                          {isNewMember ? (
                            <>
                              {guest.photo ? (
                                <img
                                  src={URL.createObjectURL(guest.photo)}
                                  alt={guest.name || 'New member'}
                                  style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '10px' }}
                                />
                              ) : (
                                <div style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '10px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleGuestChange(editingClan, guest.id, 'photo', file);
                                  }
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <img
                                src={guest.photoUrl || 'https://placehold.co/100'}
                                alt={guest.name}
                                style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '10px' }}
                              />
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const storage = getStorage();
                                    const photoRef = ref(storage, `guest_photos/${guest.id}`);
                                    await uploadBytes(photoRef, file);
                                    const url = await getDownloadURL(photoRef);

                                    // Update Firestore directly
                                    const guestRef = doc(db, 'invites', guest.id);
                                    await updateDoc(guestRef, { photoUrl: url });

                                    // Update local state
                                    handleGuestChange(editingClan, guest.id, 'photoUrl', url);
                                  }
                                }}
                              />
                            </>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={guest.name || ''}
                            onChange={e => handleGuestChange(editingClan, guest.id, 'name', e.target.value)}
                            className="input-field"
                            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                            placeholder={isNewMember ? "Enter name (required)" : ""}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={guest.phoneNumber || ''}
                            onChange={e => handleGuestChange(editingClan, guest.id, 'phoneNumber', e.target.value)}
                            className="input-field"
                            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                          />
                        </td>
                        <td>
                          <select
                            value={guest.role || 'guest'}
                            onChange={e => handleGuestChange(editingClan, guest.id, 'role', e.target.value)}
                            className="select-field"
                            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                          >
                            <option value="guest">Guest</option>
                            <option value="bride">Bride</option>
                            <option value="groom">Groom</option>
                            <option value="bestman">Best Man</option>
                            <option value="groomsman">Groomsman</option>
                            <option value="bridesmaid">Bridesmaid</option>
                            <option value="maidofhonor">Maid of Honor</option>
                            <option value="paigeboy">Paige Boy</option>
                            <option value="flowergirl">Flower Girl</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={guest.tableNumber || ''}
                            onChange={e => handleGuestChange(editingClan, guest.id, 'tableNumber', e.target.value)}
                            className="input-field"
                            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                          />
                        </td>
                        <td>
                          <select
                            value={guest.ageCategory || 'adult'}
                            onChange={e => handleGuestChange(editingClan, guest.id, 'ageCategory', e.target.value)}
                            className="select-field"
                            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}
                          >
                            <option value="adult">Adult</option>
                            <option value="teen">Teen</option>
                            <option value="child">Child</option>
                            <option value="infant">Infant</option>
                          </select>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'var(--font-body)', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={guest.eveningGuest === true}
                              onChange={e => handleGuestChange(editingClan, guest.id, 'eveningGuest', e.target.checked)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            Evening
                          </label>
                        </td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                          color: 'var(--text-secondary)'
                        }}>
                          {guest.lastLogin ? (() => {
                            const loginDate = guest.lastLogin?.toDate ? guest.lastLogin.toDate() : new Date(guest.lastLogin);
                            return loginDate.toLocaleString();
                          })() : '—'}
                        </td>
                        <td>
                          {isNewMember ? (
                            <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                              <button 
                                onClick={() => handleSaveNewMember(editingClan, guest.id)}
                                className="button-primary"
                                style={{
                                  backgroundColor: 'var(--button-success)',
                                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                  padding: '4px 8px'
                                }}
                              >Save</button>
                              <button 
                                onClick={() => handleRemoveNewMember(editingClan, guest.id)}
                                className="button-primary"
                                style={{
                                  backgroundColor: 'var(--button-danger)',
                                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                  padding: '4px 8px'
                                }}
                              >Remove</button>
                            </div>
                          ) : (
                            <button 
                              onClick={() => handleDeleteGuest(editingClan, guest.id)}
                              className="button-primary"
                              style={{
                                backgroundColor: 'var(--button-danger)',
                                fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)'
                              }}
                            >Delete</button>
                          )}
                        </td>
                      </tr>
                      {!isNewMember && (
                        <tr>
                          <td colSpan="9" style={{ padding: '10px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}>
                              <thead>
                                <tr>
                                  <th style={{ 
                                    padding: '5px', 
                                    textAlign: 'left',
                                    fontFamily: 'var(--font-body)',
                                    color: 'var(--text-secondary)',
                                    fontWeight: '600',
                                    fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                  }}>RSVP</th>
                                  {!isEveningGuest && (
                                    <>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Starter</th>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Main</th>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Dessert</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td style={{ padding: '5px' }}>
                                    <select
                                      value={guest.rsvp || ''}
                                      onChange={e => handleGuestChange(editingClan, guest.id, 'rsvp', e.target.value)}
                                      className="select-field"
                                      style={{ 
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                        width: '100%',
                                        color: guest.rsvp === 'yes' ? 'var(--button-success)' : guest.rsvp === 'no' ? 'var(--button-danger)' : 'var(--text-secondary)'
                                      }}
                                    >
                                      <option value="">Not set</option>
                                      <option value="yes">Yes</option>
                                      <option value="no">No</option>
                                    </select>
                                  </td>
                                  {!isEveningGuest && (
                                    <>
                                      <td style={{ padding: '5px' }}>
                                        <select
                                          value={guest.menuChoices?.starter || ''}
                                          onChange={e => handleMenuChoiceChange(editingClan, guest.id, 'starter', e.target.value)}
                                          className="select-field"
                                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', width: '100%' }}
                                        >
                                          <option value="">Select starter</option>
                                          {getMenuOptions('starter', guest.ageCategory).map((item, i) => (
                                            <option key={i} value={item.name}>{item.name}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td style={{ padding: '5px' }}>
                                        <select
                                          value={guest.menuChoices?.main || ''}
                                          onChange={e => handleMenuChoiceChange(editingClan, guest.id, 'main', e.target.value)}
                                          className="select-field"
                                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', width: '100%' }}
                                        >
                                          <option value="">Select main</option>
                                          {getMenuOptions('main', guest.ageCategory).map((item, i) => (
                                            <option key={i} value={item.name}>{item.name}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td style={{ padding: '5px' }}>
                                        <select
                                          value={guest.menuChoices?.dessert || ''}
                                          onChange={e => handleMenuChoiceChange(editingClan, guest.id, 'dessert', e.target.value)}
                                          className="select-field"
                                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', width: '100%' }}
                                        >
                                          <option value="">Select dessert</option>
                                          {getMenuOptions('dessert', guest.ageCategory).map((item, i) => (
                                            <option key={i} value={item.name}>{item.name}</option>
                                          ))}
                                        </select>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                            <div style={{ marginTop: '10px' }}>
                              <label style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '8px',
                                fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                fontFamily: 'var(--font-body)',
                                fontWeight: '600',
                                color: 'var(--text-secondary)'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={(guest._dietaryRequirementsInput?.hasRequirements ?? guest.hasDietaryRequirements) || false}
                                  onChange={e => handleDietaryRequirementsInputChange(editingClan, guest.id, e.target.checked, guest._dietaryRequirementsInput?.requirements || guest.dietaryRequirements || '')}
                                  style={{ 
                                    marginRight: '8px',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer'
                                  }}
                                />
                                Has Dietary Requirements
                              </label>
                              {((guest._dietaryRequirementsInput?.hasRequirements ?? guest.hasDietaryRequirements) || guest.dietaryRequirements) && (
                                <>
                                  <textarea
                                    value={(guest._dietaryRequirementsInput?.requirements ?? guest.dietaryRequirements) || ''}
                                    onChange={e => handleDietaryRequirementsInputChange(editingClan, guest.id, (guest._dietaryRequirementsInput?.hasRequirements ?? guest.hasDietaryRequirements) || false, e.target.value)}
                                    placeholder="Enter dietary requirements..."
                                    className="input-field"
                                    style={{
                                      width: '100%',
                                      minHeight: '60px',
                                      fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                      fontFamily: 'var(--font-body)',
                                      resize: 'vertical',
                                      padding: '8px',
                                      marginBottom: '8px'
                                    }}
                                  />
                                  <button
                                    onClick={() => handleDietaryRequirementsSubmit(editingClan, guest.id)}
                                    className="button-primary"
                                    style={{
                                      fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                      padding: '6px 12px'
                                    }}
                                  >
                                    Save Dietary Requirements
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {isNewMember && (
                        <tr>
                          <td colSpan="7" style={{ padding: '10px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div style={{ 
                              fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                              fontFamily: 'var(--font-body)',
                              color: 'var(--text-secondary)',
                              fontStyle: 'italic'
                            }}>
                              Fill in the required fields (name and age category) and click "Save" to add this member to the family group.
                            </div>
                            <textarea
                              value={guest.comments || ''}
                              onChange={e => handleGuestChange(editingClan, guest.id, 'comments', e.target.value)}
                              placeholder="Comments (optional)"
                              className="input-field"
                              style={{
                                width: '100%',
                                minHeight: '60px',
                                fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                fontFamily: 'var(--font-body)',
                                resize: 'vertical',
                                padding: '8px',
                                marginTop: '10px'
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => handleAddNewMember(editingClan)}
                  className="button-secondary"
                  style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
                >
                  Add New Member
                </button>
                <button 
                  onClick={async () => {
                  const updates = editedGroups[editingClan];
                  // Filter out new members (they need to be saved separately)
                  const existingGuests = updates.filter(g => !g._isNew);
                  const newGuests = updates.filter(g => g._isNew);
                  
                  // Save existing guests
                  for (let guest of existingGuests) {
                    // Skip if guest doesn't have an id (shouldn't happen, but safety check)
                    if (!guest.id || guest.id.startsWith('temp-')) continue;
                    
                    const guestRef = doc(db, 'invites', guest.id);
                    const updateData = {
                      name: guest.name,
                      phoneNumber: guest.phoneNumber,
                      familyClan: editingClan,
                      tableNumber: guest.tableNumber,
                      ageCategory: guest.ageCategory,
                      role: guest.role,
                      eveningGuest: guest.eveningGuest === true,
                    };
                    if (guest.rsvp !== undefined) {
                      updateData.rsvp = guest.rsvp;
                    }
                    if (guest.menuChoices) {
                      updateData.menuChoices = guest.menuChoices;
                    }
                    if (guest.hasDietaryRequirements !== undefined) {
                      updateData.hasDietaryRequirements = guest.hasDietaryRequirements;
                    }
                    if (guest.dietaryRequirements !== undefined) {
                      updateData.dietaryRequirements = guest.dietaryRequirements;
                    }
                    await updateDoc(guestRef, updateData);
                  }
                  
                  // Warn if there are unsaved new members
                  if (newGuests.length > 0) {
                    alert(`Please save or remove ${newGuests.length} new member(s) before saving changes.`);
                    return;
                  }
                  
                  await fetchGuests();
                  setEditingClan(null);
                  setOriginalEditingClan(null);
                }}
                className="button-primary"
                style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
              >
                  Save Changes
                </button>
              </div>
            </div>
          );
        } else {
          // Sort original group
          group.sort((a, b) => (ageRank[b.ageCategory] || 0) - (ageRank[a.ageCategory] || 0));

          return (
            <div key={clan} className="card" style={{ marginBottom: '40px', overflowX: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '10px', flexWrap: 'wrap' }}>
                <h3 style={{ 
                  margin: 0,
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>{clan}</h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => {
                      setEditingClan(clan);
                      setOriginalEditingClan(clan);
                      setEditedGroups(prev => ({ ...prev, [clan]: [...group] }));
                    }}
                    className="button-primary"
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1rem)' }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteFamilyGroup(clan)}
                    className="button-primary"
                    style={{ 
                      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                      backgroundColor: 'var(--button-danger)'
                    }}
                  >
                    Delete Family Group
                  </button>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Name</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Phone Number</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Role</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Table Number</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Age Category</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Evening</th>
                    <th style={{ 
                      padding: '10px', 
                      textAlign: 'left',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)',
                      fontWeight: '600'
                    }}>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map(guest => (
                    <React.Fragment key={guest.id}>
                      <tr style={{ verticalAlign: 'middle' }}>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>
                          <img
                            src={guest.photoUrl || 'https://placehold.co/100'}
                            alt={guest.name}
                            style={{ width: '30px', height: '30px', borderRadius: '50%', marginRight: '10px', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                          />
                          {guest.name}
                        </td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>{guest.phoneNumber}</td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>{guest.role || '—'}</td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>{guest.tableNumber || '—'}</td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>{guest.ageCategory || '—'}</td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          color: 'var(--text-primary)'
                        }}>{guest.eveningGuest ? 'Yes' : '—'}</td>
                        <td style={{ 
                          padding: '10px',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                          color: 'var(--text-secondary)'
                        }}>
                          {guest.lastLogin ? (() => {
                            const loginDate = guest.lastLogin?.toDate ? guest.lastLogin.toDate() : new Date(guest.lastLogin);
                            return loginDate.toLocaleString();
                          })() : '—'}
                        </td>
                      </tr>
                      {(guest.rsvp || (!guest.eveningGuest && guest.menuChoices) || guest.dietaryRequirements) && (
                        <tr>
                          <td colSpan="7" style={{ padding: '10px', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}>
                              <thead>
                                <tr>
                                  <th style={{ 
                                    padding: '5px', 
                                    textAlign: 'left',
                                    fontFamily: 'var(--font-body)',
                                    color: 'var(--text-secondary)',
                                    fontWeight: '600',
                                    fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                  }}>RSVP</th>
                                  {!guest.eveningGuest && (
                                    <>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Starter</th>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Main</th>
                                      <th style={{ 
                                        padding: '5px', 
                                        textAlign: 'left',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: '600',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>Dessert</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td style={{ 
                                    padding: '5px',
                                    fontFamily: 'var(--font-body)',
                                    color: guest.rsvp === 'yes' ? 'var(--button-success)' : guest.rsvp === 'no' ? 'var(--button-danger)' : 'var(--text-secondary)',
                                    fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                  }}>
                                    {guest.rsvp === 'yes' ? '✓ Yes' : guest.rsvp === 'no' ? '✗ No' : '—'}
                                  </td>
                                  {!guest.eveningGuest && (
                                    <>
                                      <td style={{ 
                                        padding: '5px',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>
                                        {guest.menuChoices?.starter || '—'}
                                      </td>
                                      <td style={{ 
                                        padding: '5px',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>
                                        {guest.menuChoices?.main || '—'}
                                      </td>
                                      <td style={{ 
                                        padding: '5px',
                                        fontFamily: 'var(--font-body)',
                                        color: 'var(--text-secondary)',
                                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)'
                                      }}>
                                        {guest.menuChoices?.dessert || '—'}
                                      </td>
                                    </>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                            {guest.dietaryRequirements && (
                              <div style={{ 
                                marginTop: '10px',
                                padding: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '4px',
                                fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                                fontFamily: 'var(--font-body)',
                                color: 'var(--text-secondary)'
                              }}>
                                <strong>Dietary Requirements:</strong> {guest.dietaryRequirements}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      })}
      <div style={{ marginTop: '40px', textAlign: 'center', padding: '20px' }}>
        <button
          onClick={() => {
            // Create print window
            const printWindow = window.open('', '_blank');
            const printContent = generatePrintContent(guests, groupedGuests);
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.onload = () => {
              printWindow.print();
            };
          }}
          className="button-primary"
          style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
            padding: '12px 24px',
            fontWeight: '600'
          }}
        >
          Print Guest List for Venue (PDF)
        </button>
      </div>
      
      {/* Failed Login Attempts Section */}
      <div className="card" style={{ marginTop: '40px' }}>
        <h3 style={{ 
          margin: '0 0 20px 0',
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
          fontWeight: '600',
          color: 'var(--text-primary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Failed Login Attempts
        </h3>
        {failedLogins.length === 0 ? (
          <p style={{ 
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
          }}>
            No failed login attempts recorded.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ 
                  padding: '10px', 
                  textAlign: 'left',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}>Phone Number</th>
                <th style={{ 
                  padding: '10px', 
                  textAlign: 'left',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}>Name</th>
                <th style={{ 
                  padding: '10px', 
                  textAlign: 'left',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}>Type</th>
                <th style={{ 
                  padding: '10px', 
                  textAlign: 'left',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                  color: 'var(--text-primary)',
                  fontWeight: '600'
                }}>Attempted At</th>
              </tr>
            </thead>
            <tbody>
              {failedLogins.map(failedLogin => {
                const attemptedDate = failedLogin.attemptedAt?.toDate ? failedLogin.attemptedAt.toDate() : new Date(failedLogin.attemptedAt || 0);
                const typeLabel = failedLogin.type === 'not_on_list' 
                  ? 'Not on guest list' 
                  : failedLogin.type === 'code_not_used' 
                    ? 'Code requested but not used' 
                    : 'Unknown';
                return (
                  <tr key={failedLogin.id} style={{ verticalAlign: 'middle' }}>
                    <td style={{ 
                      padding: '10px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)'
                    }}>
                      {failedLogin.phoneNumber || '—'}
                    </td>
                    <td style={{ 
                      padding: '10px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-primary)'
                    }}>
                      {failedLogin.name || '—'}
                    </td>
                    <td style={{ 
                      padding: '10px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-secondary)'
                    }}>
                      {typeLabel}
                    </td>
                    <td style={{ 
                      padding: '10px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                      color: 'var(--text-secondary)'
                    }}>
                      {attemptedDate.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Generate print-friendly HTML content without phone numbers (day guests only — venue needs meal counts)
const generatePrintContent = (guests, groupedGuests) => {
  // Filter to day guests who RSVP'd yes (evening guests are not included in venue/catering export)
  const rsvpYesGuests = guests.filter(guest => guest.rsvp === 'yes' && !guest.eveningGuest);
  
  const ageSummary = rsvpYesGuests.reduce((acc, guest) => {
    const category = guest.ageCategory || 'unknown';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  // Filter groupedGuests to only include day guests with RSVP yes (no evening guests for venue export)
  const filteredGroupedGuests = {};
  Object.entries(groupedGuests).forEach(([clan, group]) => {
    const rsvpYesDayGroup = group.filter(guest => guest.rsvp === 'yes' && !guest.eveningGuest);
    if (rsvpYesDayGroup.length > 0) {
      filteredGroupedGuests[clan] = rsvpYesDayGroup;
    }
  });

  const sortedClans = Object.entries(filteredGroupedGuests)
    .sort(([clanA], [clanB]) => clanA.localeCompare(clanB));

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Wedding Guest List</title>
      <style>
        @media print {
          @page {
            margin: 1cm;
          }
          body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            color: #000;
            background: #fff;
          }
        }
        body {
          font-family: Arial, sans-serif;
          color: #000;
          background: #fff;
          padding: 20px;
        }
        h1 {
          text-align: center;
          margin-bottom: 10px;
          font-size: 24px;
        }
        .summary {
          text-align: center;
          margin-bottom: 30px;
          font-size: 14px;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }
        .clan-section {
          margin-bottom: 30px;
          page-break-inside: avoid;
        }
        .clan-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 10px;
          text-transform: uppercase;
          border-bottom: 1px solid #ccc;
          padding-bottom: 5px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
          font-size: 12px;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .sub-table {
          margin-top: 10px;
          font-size: 11px;
        }
        .sub-table th {
          background-color: #f9f9f9;
        }
        .rsvp-yes {
          color: #008000;
        }
        .rsvp-no {
          color: #cc0000;
        }
      </style>
    </head>
    <body>
      <h1>Wedding Guest List</h1>
      <div class="summary">
        <strong>Totals:</strong> Adults: ${ageSummary.adult || 0} | Teens: ${ageSummary.teen || 0} | Children: ${ageSummary.child || 0} | Infants: ${ageSummary.infant || 0}
      </div>
  `;

  sortedClans.forEach(([clan, group]) => {
    const ageRank = { adult: 4, teen: 3, child: 2, infant: 1 };
    const sortedGroup = [...group].sort((a, b) => (ageRank[b.ageCategory] || 0) - (ageRank[a.ageCategory] || 0));

    html += `
      <div class="clan-section">
        <div class="clan-title">${clan}</div>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Table Number</th>
              <th>Age Category</th>
              <th>RSVP</th>
              <th>Starter</th>
              <th>Main</th>
              <th>Dessert</th>
              <th>Dietary Requirements</th>
            </tr>
          </thead>
          <tbody>
    `;

    sortedGroup.forEach(guest => {
      const rsvpClass = guest.rsvp === 'yes' ? 'rsvp-yes' : guest.rsvp === 'no' ? 'rsvp-no' : '';
      const rsvpText = guest.rsvp === 'yes' ? '✓ Yes' : guest.rsvp === 'no' ? '✗ No' : '—';
      
      html += `
            <tr>
              <td>${guest.name || '—'}</td>
              <td>${guest.role || '—'}</td>
              <td>${guest.tableNumber || '—'}</td>
              <td>${guest.ageCategory || '—'}</td>
              <td class="${rsvpClass}">${rsvpText}</td>
              <td>${guest.menuChoices?.starter || '—'}</td>
              <td>${guest.menuChoices?.main || '—'}</td>
              <td>${guest.menuChoices?.dessert || '—'}</td>
              <td>${guest.dietaryRequirements || '—'}</td>
            </tr>
      `;
    });

    html += `
          </tbody>
        </table>
      </div>
    `;
  });

  html += `
    </body>
    </html>
  `;

  return html;
};

export default GuestList;